import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {classNames} from "primereact/utils";
import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";

import type {RevaSpecObjectInputRow} from "@reva/shared/reva-spec";

import {useSelectByCurrentLanguage} from "../../../hooks/languageHooks";

interface RegisterOutputTableProps {
    columns: (RevaSpecObjectInputRow | undefined)[];
    values: Array<Record<string, unknown>>;
    footer?: ReactNode;
    footerColumnGroup?: ReactNode;
    /** Formatierter Wert, oder undefined für die unveränderte Ausgabe. */
    formatValue?: (parameter: string, value: unknown) => string | undefined;
    /** Spalten mit Geldbeträgen, die ohne Umbruch Platz für -12.345.678 € bekommen. */
    valueParameters?: string[];
}

export const VALUE_CELL_CLASS = "whitespace-nowrap min-w-[8rem]";

export function RegisterOutputTable({
    columns,
    values,
    footer,
    footerColumnGroup,
    formatValue,
    valueParameters,
}: RegisterOutputTableProps) {
    const {t} = useTranslation();
    const getByLanguage = useSelectByCurrentLanguage();

    const cell = (parameter: string, value: unknown) =>
        formatValue?.(parameter, value) ??
        (value != null && value !== "" ? String(value) : t("object.tab_register.no_entry"));

    if (!values.length) return null;
    return (
        <DataTable
            value={values}
            size="small"
            className="mb-4"
            footer={footer}
            footerColumnGroup={footerColumnGroup}
            // Zellen formatieren sprachabhängig, unveränderte Zeilen würden beim Sprachwechsel nicht neu gerendert
            cellMemo={false}
        >
            {columns
                .filter((column): column is RevaSpecObjectInputRow => column !== undefined)
                .map(({parameter, details}, index) => {
                    const className = classNames(
                        index !== 0 && "p-align-right",
                        valueParameters?.includes(parameter) && VALUE_CELL_CLASS,
                    );
                    return (
                        <Column
                            key={parameter}
                            field={parameter}
                            header={getByLanguage(details?.nameDe ?? "", details?.nameEn ?? "")}
                            bodyClassName={className}
                            headerClassName={className}
                            body={(row: Record<string, unknown>) => cell(parameter, row[parameter])}
                        />
                    );
                })}
        </DataTable>
    );
}
